import { useCallback, useEffect, useRef, useState } from "react";
import { applySend, isSendable, mergeItems, sendSummary } from "../../../lib/artemis/scratchpad";

// The open conversation's scratchpad: its items, which are ticked, and Send. api holds the
// dispatched Scratch actions; conversationId is null for a new chat until its first message.
// Drafts from a reply arrive through merge() as their step events stream in.
const useScratchpad = (api, conversationId) => {
  const [items, setItems] = useState([])
  const [checked, setChecked] = useState(() => new Set())
  // 'idle', 'loading', 'loaded' or 'failed'.
  const [state, setState] = useState('idle')
  const [sending, setSending] = useState(false)
  // Why an item didn't send, by item id, until it's sent or removed.
  const [failed, setFailed] = useState({})
  const current = useRef(conversationId)
  current.current = conversationId

  const load = useCallback(() => {
    if (!conversationId) { setItems([]); setState('idle'); return Promise.resolve() }
    setState('loading')
    const id = conversationId
    return api.fetch(id)
      .then(page => { if (current.current === id) { setItems(mergeItems([], (page && page.Items) || [])); setState('loaded') } })
      .catch(() => { if (current.current === id) setState('failed') })
  }, [api, conversationId])

  useEffect(() => {
    setChecked(new Set())
    setFailed({})
    load()
  }, [conversationId])

  // New drafts are ticked, so Send picks up what Artemis just found.
  const merge = useCallback(incoming => {
    if (!incoming || incoming.length === 0) return
    setItems(list => mergeItems(list, incoming))
    setChecked(set => { const next = new Set(set); incoming.forEach(i => i.Status !== 'sent' && isSendable(i) && next.add(i.Id)); return next })
  }, [])

  const toggle = useCallback(id => setChecked(set => { const next = new Set(set); next.has(id) ? next.delete(id) : next.add(id); return next }), [])

  const sendable = items.filter(i => i.Status !== 'sent' && isSendable(i))
  const allChecked = sendable.length > 0 && sendable.every(i => checked.has(i.Id))
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(sendable.map(i => i.Id)))

  const remove = id => {
    if (!conversationId) return Promise.resolve()
    const before = items
    setItems(list => list.filter(i => i.Id !== id))
    setChecked(set => { const next = new Set(set); next.delete(id); return next })
    return api.remove(conversationId, id).catch(err => { setItems(before); throw err })
  }

  // PATCH a draft (e.g. its waypoint type); clears the item's Send failure.
  const update = (id, changes) => {
    if (!conversationId) return Promise.resolve(null)
    return api.update(conversationId, id, changes).then(item => {
      if (item) setItems(list => mergeItems(list, [item]))
      setFailed(f => { const next = { ...f }; delete next[id]; return next })
      return item
    })
  }

  const add = item => (conversationId ? api.add(conversationId, item).then(created => { merge([created]); return created }) : Promise.reject(new Error('no_conversation')))

  // Sends the ticked drafts that can be sent; resolves with the toast text and the response.
  const send = () => {
    const ids = sendable.filter(i => checked.has(i.Id)).map(i => i.Id)
    if (!conversationId || ids.length === 0 || sending) return Promise.resolve(null)
    setSending(true)
    return api.send(conversationId, ids)
      .then(response => {
        const result = applySend(items, response)
        setItems(result.items)
        setFailed(result.failed)
        setChecked(set => { const next = new Set(set); (response.Sent || []).forEach(s => next.delete(s.ItemId)); return next })
        return { text: sendSummary(response), response }
      })
      .finally(() => setSending(false))
  }

  return {
    items, checked, state, sending, failed, load, merge, toggle, toggleAll, allChecked, remove, add, update, send,
    checkedCount: sendable.filter(i => checked.has(i.Id)).length
  }
}

export default useScratchpad
